/**
 * ExitEditModeDialog Component
 *
 * Warning dialog shown when user tries to exit edit mode with pending changes.
 * Offers options to save changes, discard changes, or cancel (stay in edit mode).
 */

import { AlertTriangle, Save, Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../../components/ui/alert-dialog';
import { Button } from '../../../../components/ui/button';

type ExitEditModeDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pendingChangeCount: number;
  onSave: () => void;
  onDiscard: () => void;
};

export function ExitEditModeDialog({
  open,
  onOpenChange,
  pendingChangeCount,
  onSave,
  onDiscard,
}: ExitEditModeDialogProps) {
  const handleSave = () => {
    onOpenChange(false);
    // Delay opening save dialog to ensure this dialog fully closes first
    // This prevents overlay stacking issues with Radix AlertDialog
    setTimeout(onSave, 150);
  };

  const handleDiscard = () => {
    onOpenChange(false);
    onDiscard();
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="exit-edit-mode-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-warning" />
            Unsaved changes
          </AlertDialogTitle>
          <AlertDialogDescription>
            You have {pendingChangeCount} unsaved change{pendingChangeCount !== 1 ? 's' : ''}.
            Would you like to save or discard them before exiting edit mode?
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="exit-edit-mode-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDiscard}
            data-testid="exit-edit-mode-discard"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Discard
          </Button>
          <Button
            onClick={handleSave}
            data-testid="exit-edit-mode-save"
          >
            <Save className="h-4 w-4 mr-2" />
            Save
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
